import { supabase } from '../../lib/supabase'

/**
 * docs/product/coverage-and-insights.md's "Staffing targets": a supervisor
 * configures minimum staffing by date or weekday, shift, assignment, team,
 * and qualification. Held in muster.staffing_targets, written and removed
 * straight from the browser under row-level security
 * (20260925000000_staffing_target_writes.sql), each change an audit event.
 * StaffingTarget is that row read back with its catalog names, the shape
 * resolveTarget below matches against.
 */
export type TargetScope = 'date' | 'weekday'

export type StaffingTarget = {
  id: string
  scope: TargetScope
  date?: string
  weekday?: string
  shift: string
  team: string
  teamId: string
  assignment?: string
  qualification?: string
  minimumHeadcount: number
  effectiveStart: string
  effectiveEnd?: string
  createdAt: string
}

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

// Date.UTC rather than `new Date(dateISO)`, so a caller in any timezone
// gets the same weekday back for the same calendar date: parsing a bare
// "YYYY-MM-DD" string as local time can land on the wrong side of
// midnight west of UTC.
export function weekdayNameFor(dateISO: string): string {
  const [year, month, day] = dateISO.split('-').map(Number)
  return WEEKDAY_NAMES[new Date(Date.UTC(year, month - 1, day)).getUTCDay()]
}

// The weekdays from one to another inclusive, Monday first, so a supervisor
// can set one target for "Monday to Friday". The same day twice is that day
// alone; a range that ends before it starts is empty.
export function weekdaysBetween(from: string, through: string): string[] {
  const week = [...WEEKDAY_NAMES.slice(1), WEEKDAY_NAMES[0]]
  const start = week.indexOf(from)
  const end = week.indexOf(through)
  if (start < 0 || end < start) return []
  return week.slice(start, end + 1)
}

type TargetRow = {
  id: string
  date: string | null
  weekday: number | null
  team_id: string
  minimum_available: number
  effective_from: string
  effective_to: string | null
  created_at: string
  teams: { name: string } | null
  shift_templates: { name: string } | null
  assignments: { name: string } | null
  qualifications: { name: string } | null
}

const TARGET_COLUMNS =
  'id, date, weekday, team_id, minimum_available, effective_from, effective_to, created_at, teams(name), shift_templates(name), assignments(name), qualifications(name)'

function toTarget(row: TargetRow): StaffingTarget {
  return {
    id: row.id,
    scope: row.date ? 'date' : 'weekday',
    date: row.date ?? undefined,
    weekday: row.weekday === null ? undefined : WEEKDAY_NAMES[row.weekday],
    shift: row.shift_templates?.name ?? '',
    team: row.teams?.name ?? '',
    teamId: row.team_id,
    assignment: row.assignments?.name ?? undefined,
    qualification: row.qualifications?.name ?? undefined,
    minimumHeadcount: row.minimum_available,
    effectiveStart: row.effective_from,
    effectiveEnd: row.effective_to ?? undefined,
    createdAt: row.created_at,
  }
}

// Every target the signed-in account can read: a supervisor's own teams',
// an administrator's every team's (staffing_targets_read_team). Callers
// narrow to one team by name or id.
export async function getStaffingTargets(): Promise<StaffingTarget[]> {
  const { data, error } = await supabase.from('staffing_targets').select(TARGET_COLUMNS).order('created_at', { ascending: true })
  if (error || !data) return []
  return (data as unknown as TargetRow[]).map(toTarget)
}

export type NewStaffingTarget = {
  teamId: string
  shiftTemplateId: string
  scope: TargetScope
  date?: string
  weekday?: string
  assignmentId?: string
  qualificationId?: string
  minimumHeadcount: number
  effectiveStart: string
  effectiveEnd?: string
}

// Returns the stored target, or null when the write did not land, the same
// contract the session-backed store this replaced gave: the caller needs to
// know before it confirms a target that was not actually recorded. A
// qualification is a floor on the qualified people among those available,
// so minimum_qualified carries the same number as minimum_available.
export async function addStaffingTarget(userId: string, target: NewStaffingTarget): Promise<StaffingTarget | null> {
  const { data, error } = await supabase
    .from('staffing_targets')
    .insert({
      team_id: target.teamId,
      shift_template_id: target.shiftTemplateId,
      date: target.scope === 'date' ? target.date : null,
      weekday: target.scope === 'weekday' ? WEEKDAY_NAMES.indexOf(target.weekday ?? '') : null,
      assignment_id: target.assignmentId ?? null,
      required_qualification_id: target.qualificationId ?? null,
      minimum_available: target.minimumHeadcount,
      minimum_qualified: target.qualificationId ? target.minimumHeadcount : null,
      effective_from: target.effectiveStart,
      effective_to: target.effectiveEnd ?? null,
      created_by: userId,
    })
    .select(TARGET_COLUMNS)
    .single()
  if (error || !data) return null
  return toTarget(data as unknown as TargetRow)
}

export async function removeStaffingTarget(id: string): Promise<boolean> {
  const { error } = await supabase.from('staffing_targets').delete().eq('id', id)
  return !error
}

export type TargetCatalog = {
  shift: { id: string; name: string; time: string } | null
  assignments: { id: string; name: string }[]
  qualifications: { id: string; name: string }[]
}

export type ShiftRow = { id: string; name: string; start_time: string; end_time: string; team_id: string | null }

// "07:00:00" (a Postgres time) as the board's own raw "0700" label form.
function clock(time: string): string {
  return time.slice(0, 5).replace(':', '')
}

// The shift a target for this team is written against. The coverage view
// matches a target to a schedule by team and shift, so it has to be the shift
// the team's people work: the one most of its current placements name. Only
// when no placement names an active shift does it fall back to the team's own
// template, then to an office-wide one.
export function shiftFor(teamId: string, shiftRows: ShiftRow[], placedShiftIds: (string | null)[]): ShiftRow | undefined {
  const counts = new Map<string, number>()
  for (const id of placedShiftIds) {
    if (id && shiftRows.some((row) => row.id === id)) counts.set(id, (counts.get(id) ?? 0) + 1)
  }
  const placed = Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]?.[0]
  return shiftRows.find((row) => row.id === placed) ?? shiftRows.find((row) => row.team_id === teamId) ?? shiftRows[0]
}

// What a supervisor can choose from when creating a target for one team:
// the shift the team works (see shiftFor), the
// assignments open to it (office-wide or its own), and every active
// qualification.
export async function getTargetCatalog(teamId: string): Promise<TargetCatalog> {
  const today = new Date().toISOString().slice(0, 10)
  const [shifts, placements, assignments, qualifications] = await Promise.all([
    supabase.from('shift_templates').select('id, name, start_time, end_time, team_id').eq('active', true),
    supabase
      .from('employee_assignments')
      .select('shift_template_id')
      .eq('team_id', teamId)
      .eq('active', true)
      .lte('effective_from', today)
      .or(`effective_to.is.null,effective_to.gte.${today}`),
    supabase.from('assignments').select('id, name, team_id').eq('active', true).order('name', { ascending: true }),
    supabase.from('qualifications').select('id, name').eq('active', true).order('name', { ascending: true }),
  ])
  const shiftRows = ((shifts.data ?? []) as unknown as ShiftRow[]).filter((row) => row.team_id === null || row.team_id === teamId)
  const shift = shiftFor(teamId, shiftRows, ((placements.data ?? []) as { shift_template_id: string | null }[]).map((row) => row.shift_template_id))
  return {
    shift: shift ? { id: shift.id, name: shift.name, time: `${clock(shift.start_time)}–${clock(shift.end_time)}` } : null,
    assignments: ((assignments.data ?? []) as unknown as { id: string; name: string; team_id: string | null }[])
      .filter((row) => row.team_id === null || row.team_id === teamId)
      .map((row) => ({ id: row.id, name: row.name })),
    qualifications: (qualifications.data ?? []) as unknown as { id: string; name: string }[],
  }
}

// Whether a target is in force on a date: inside its effective window, and
// on its own date or weekday.
function appliesOn(target: StaffingTarget, params: { date: string; weekday: string }): boolean {
  if (target.effectiveStart > params.date) return false
  if (target.effectiveEnd && target.effectiveEnd < params.date) return false
  return target.scope === 'date' ? target.date === params.date : target.weekday === params.weekday
}

// A target that names no assignment ("Any assignment" on the form) is a
// minimum for the whole shift, measured against the shift's total, and is
// left out of a per-assignment lookup like this one: the daily assignment
// board's coverage strip and the dashboard's per-assignment gaps are
// per-assignment tiles, so a shift-wide minimum of 5 would otherwise become a
// 5-person floor for every assignment at once, a multiplied requirement no
// supervisor configuring "Any assignment" meant. resolveShiftTarget below
// reads those targets.
function matches(
  target: StaffingTarget,
  params: { date: string; weekday: string; shift: string; team: string; assignment: string },
): boolean {
  if (target.shift !== params.shift || target.team !== params.team) return false
  if (!target.assignment || target.assignment !== params.assignment) return false
  return appliesOn(target, params)
}

// Lower ranks first: a date-scoped target beats a weekday-scoped one, and
// naming an assignment beats a shift-only target, per
// docs/product/coverage-and-insights.md's "The most specific target wins."
function targetRank(target: StaffingTarget): number {
  return (target.scope === 'date' ? 0 : 2) + (target.assignment ? 0 : 1)
}

// The winning target for one date, shift, team, and assignment, or
// undefined when nothing configured covers it. A tie between two targets
// of the same specificity (two date-scoped, assignment-naming targets
// covering the same date, say) settles on whichever was created most
// recently, since a supervisor adding a new target expects it to take
// hold over an older one rather than being silently shadowed by it.
export function resolveTarget(
  targets: StaffingTarget[],
  params: { date: string; weekday: string; shift: string; team: string; assignment: string },
): StaffingTarget | undefined {
  return targets.filter((target) => matches(target, params)).reduce<StaffingTarget | undefined>((best, candidate) => {
    if (!best) return candidate
    const candidateRank = targetRank(candidate)
    const bestRank = targetRank(best)
    if (candidateRank < bestRank) return candidate
    if (candidateRank === bestRank && candidate.createdAt > best.createdAt) return candidate
    return best
  }, undefined)
}

// The winning whole-shift target for one date, shift, and team: the minimum
// on the shift's total, whatever the assignment. A date-scoped target beats
// a weekday one, and a tie settles on the most recently created, the same as
// resolveTarget.
export function resolveShiftTarget(
  targets: StaffingTarget[],
  params: { date: string; weekday: string; shift: string; team: string },
): StaffingTarget | undefined {
  return targets
    .filter((target) => !target.assignment && target.shift === params.shift && target.team === params.team && appliesOn(target, params))
    .reduce<StaffingTarget | undefined>((best, candidate) => {
      if (!best) return candidate
      const candidateRank = candidate.scope === 'date' ? 0 : 1
      const bestRank = best.scope === 'date' ? 0 : 1
      if (candidateRank < bestRank) return candidate
      if (candidateRank === bestRank && candidate.createdAt > best.createdAt) return candidate
      return best
    }, undefined)
}
